/** Abrechnungen (Screen laden/Abrechnungen): Periode blättern, Vorschau mit KPIs, „Abrechnungen erstellen“, Liste mit Methode/Status. DATEV/SEPA sind V2/Später. */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button, EmptyState, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, Grid, KpiTile, PageHeader } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatTag } from "@shared/zeit";
import { ABRECHNUNG_STATUS, exportUrl, useAbrechnungen, useAbrechnungenErstellen, useAuszahlungsanfrageAktion, useAuszahlungsanfragen, usePeriode, useVorschau } from "./geld-api";

export function Abrechnungen() {
  const [anker, setAnker] = useState<string | undefined>(undefined);
  const periode = usePeriode(anker);
  const p = periode.data;
  const von = p?.aktuell.von ?? "";
  const bis = p?.aktuell.bis ?? "";
  const vorschau = useVorschau(von, bis);
  const abrechnungen = useAbrechnungen(von, bis);
  const erstellen = useAbrechnungenErstellen();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"alle" | "ueberweisung" | "bar" | "uebertragen" | "forderung">("alle");
  const [suche, setSuche] = useState("");
  const [meldung, setMeldung] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const anfragen = useAuszahlungsanfragen();
  const anfrageAktion = useAuszahlungsanfrageAktion();
  const offeneAnfragen = (anfragen.data?.anfragen ?? []).filter((a) => a.status === "offen");

  const zeilen = useMemo(() => {
    const z = vorschau.data?.zeilen ?? [];
    return z.filter((x) => {
      if (filter === "ueberweisung" && !(x.auszahlungsweg === "ueberweisung" && x.status === "erstellt")) return false;
      if (filter === "bar" && !(x.auszahlungsweg === "bar" && x.status === "erstellt")) return false;
      if (filter !== "alle" && (filter === "uebertragen" || filter === "forderung") && x.status !== filter) return false;
      if (suche && !x.mieterName.toLowerCase().includes(suche.toLowerCase()) && !x.regale.some((r) => r.includes(suche))) return false;
      return true;
    });
  }, [vorschau.data, filter, suche]);
  const alle = vorschau.data?.zeilen ?? [];
  const kpi = {
    umsatz: alle.reduce((a, z) => a + z.umsatzCent, 0),
    artikel: alle.reduce((a, z) => a + z.artikel, 0),
    provision: alle.reduce((a, z) => a + z.provisionCent, 0),
    miete: alle.reduce((a, z) => a + z.mieteCent, 0),
    auszahlung: alle.filter((z) => z.status === "erstellt").reduce((a, z) => a + z.summeCent, 0),
    n: (st: string) => alle.filter((z) => z.status === st).length,
    weg: (w: string) => alle.filter((z) => z.status === "erstellt" && z.auszahlungsweg === w).length,
  };
  const schonErstellt = alle.filter((z) => z.vorhanden).length;

  return (
    <>
      <PageHeader
        kicker="Geld"
        title="Abrechnungen"
        sub={p ? `${p.aktuell.name} · ${formatTag(p.aktuell.von)}–${formatTag(p.aktuell.bis)} · Periode: ${p.art === "monat" ? "monatlich" : "wöchentlich"}` : ""}
        actions={
          <>
            <Button variant="quiet" icon="chevron-left" aria-label="Vorherige Periode" onClick={() => setAnker(p?.vorherige.von)} />
            <Button variant="secondary" onClick={() => setAnker(undefined)}>Aktuelle Periode</Button>
            <Button variant="quiet" icon="chevron-right" aria-label="Nächste Periode" onClick={() => setAnker(p?.naechste.von)} disabled={!p || p.naechste.von > p.heute} />
            {p ? <a href={exportUrl("abrechnungen", p.aktuell.von, p.aktuell.bis)} className="rg-btn rg-btn--secondary">Export CSV</a> : null}
            <Button
              variant="primary"
              icon="check"
              disabled={!p?.abrechenbar || alle.length === 0 || schonErstellt === alle.length}
              loading={erstellen.isPending}
              onClick={() => {
                if (!confirm(`Abrechnungen für ${p!.aktuell.name} erstellen? Danach sind die Buchungen der Periode abgeschlossen.`)) return;
                erstellen.mutate({ von, bis }, { onSuccess: (r) => setMeldung({ tone: "success", text: `${r.erstellt.length} Abrechnungen erstellt${r.uebersprungen ? `, ${r.uebersprungen} gab es schon` : ""}. Jetzt an die Mieter senden und auszahlen.` }), onError: (e) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Erstellen hat nicht geklappt." }) });
              }}
            >
              Abrechnungen erstellen
            </Button>
          </>
        }
      />
      {meldung ? <Notice tone={meldung.tone}>{meldung.text}</Notice> : null}
      {offeneAnfragen.map((a) => (
        <Notice key={a.id} tone="attention" title={`${a.mieterName} möchte ${formatEuro(a.betragCent)} ausgezahlt bekommen`} action={<span className="sc-toolbar"><Button variant="primary" loading={anfrageAktion.isPending} onClick={() => anfrageAktion.mutate({ id: a.id, aktion: "erledigen" }, { onSuccess: (r) => navigate(`/laden/abrechnungen/${(r as { abrechnungId: string }).abrechnungId}`), onError: (e) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Das hat nicht geklappt." }) })}>Zwischenabrechnung erstellen</Button><Button variant="quiet" onClick={() => { const notiz = prompt("Grund für die Ablehnung (optional):") ?? ""; anfrageAktion.mutate({ id: a.id, aktion: "ablehnen", notiz }); }}>Ablehnen</Button></span>}>
          Angefordert am {formatTag(a.erstelltAt.slice(0, 10))} · Mieternummer {a.mieternummer}. Die Zwischenabrechnung umfasst alle offenen Buchungen bis heute; danach wie gewohnt auszahlen.
        </Notice>
      ))}
      {p && !p.abrechenbar ? (
        <Notice tone="info" title={`Vorschau: Periode läuft bis ${formatTag(p.aktuell.bis)}`}>Die Zahlen ändern sich noch mit jedem Verkauf. Abrechnungen lassen sich ab {formatTag(p.naechste.von)} erstellen und an die Mieter senden.</Notice>
      ) : null}
      {schonErstellt && schonErstellt === alle.length && alle.length ? <Notice tone="success" title="Periode ist abgerechnet">Alle {alle.length} Abrechnungen sind erstellt – unten öffnen, senden und auszahlen.</Notice> : null}
      <Seitenzustand query={vorschau} lines={6}>
        {() => (
          <>
            <Grid>
              <KpiTile label="Umsatz Mieterware" value={formatEuro(kpi.umsatz)} note={`${kpi.artikel} Artikel · ${alle.length} Mieter`} />
              <KpiTile label="Provision" value={formatEuro(-kpi.provision)} note={kpi.umsatz ? `Ø ${Math.round((-kpi.provision / kpi.umsatz) * 100)} %` : undefined} />
              <KpiTile label="Miete verrechnet" value={formatEuro(-kpi.miete)} note={kpi.n("forderung") ? `${kpi.n("forderung")} offen als Forderung` : undefined} />
              <KpiTile label="Auszahlung gesamt" value={formatEuro(kpi.auszahlung)} note={`${kpi.weg("ueberweisung")} Überweisung · ${kpi.weg("bar")} bar`} />
            </Grid>
            <div className="sc-toolbar" style={{ margin: "var(--space-4) 0" }}>
              {(
                [
                  ["alle", `Alle ${alle.length}`],
                  ["ueberweisung", `Überweisung ${kpi.weg("ueberweisung")}`],
                  ["bar", `Bar ${kpi.weg("bar")}`],
                  ["uebertragen", `Übertrag ${kpi.n("uebertragen")}`],
                  ["forderung", `Forderung ${kpi.n("forderung")}`],
                ] as [typeof filter, string][]
              ).map(([k, t]) => (
                <button key={k} type="button" className="sc-pill" aria-pressed={filter === k} onClick={() => setFilter(k)}>{t}</button>
              ))}
              <span className="sc-grow" />
              <TextField label="Suche" icon="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Mieter oder Regal" />
            </div>
            {alle.length === 0 ? (
              <Card><EmptyState compact illustration="all-done" title="Nichts abzurechnen">In dieser Periode gibt es keine Buchungen im Mieterkonto.</EmptyState></Card>
            ) : (
              <Card flush>
                <div className="app-tabellen-scroll">
                  <table className="rg-table">
                    <thead><tr><th>Mieter</th><th>Regal</th><th className="is-num">Umsatz</th><th className="is-num">Provision</th><th className="is-num">Miete</th><th className="is-num">Auszahlung</th><th>Methode</th><th>Status</th><th /></tr></thead>
                    <tbody>
                      {zeilen.map((z) => {
                        const st = z.vorhanden ? ABRECHNUNG_STATUS[z.vorhanden.status] : null;
                        return (
                          <tr key={z.mieterId} className={z.vorhanden ? "rg-row--click" : undefined} onClick={() => z.vorhanden && navigate(`/laden/abrechnungen/${z.vorhanden.id}`)}>
                            <td><Link to={`/laden/mieter/${z.mieterId}`} onClick={(e) => e.stopPropagation()}>{z.mieterName}</Link><div className="sc-muted sc-small">{z.mieternummer}</div></td>
                            <td className="sc-num">{z.regale.join(", ") || "–"}</td>
                            <td className="is-num">{formatEuro(z.umsatzCent)}</td>
                            <td className="is-num">{formatEuro(z.provisionCent)}</td>
                            <td className="is-num">{formatEuro(z.mieteCent)}</td>
                            <td className="is-num sc-strong">{formatEuro(z.summeCent)}</td>
                            <td>{z.status === "erstellt" ? (z.auszahlungsweg === "bar" ? "Bar" : "Überweisung") : "–"}</td>
                            <td>{st ? <StatusBadge status={st.badge}>{st.text}</StatusBadge> : z.status === "uebertragen" ? <StatusBadge status="attention">Unter {formatEuro(vorschau.data!.mindestbetragCent)}: Übertrag</StatusBadge> : z.status === "forderung" ? <StatusBadge status="attention">Forderung {formatEuro(-z.summeCent)}</StatusBadge> : <span className="sc-muted">Vorschau</span>}</td>
                            <td>{z.vorhanden ? <Link to={`/laden/abrechnungen/${z.vorhanden.id}`} className="rg-btn rg-btn--quiet" onClick={(e) => e.stopPropagation()}>Öffnen</Link> : null}</td>
                          </tr>
                        );
                      })}
                      <tr><td className="sc-strong">Alle {alle.length} Mieter</td><td /><td className="is-num sc-strong">{formatEuro(kpi.umsatz)}</td><td className="is-num sc-strong">{formatEuro(kpi.provision)}</td><td className="is-num sc-strong">{formatEuro(kpi.miete)}</td><td className="is-num sc-strong">{formatEuro(kpi.auszahlung)}</td><td colSpan={3} /></tr>
                    </tbody>
                  </table>
                </div>
                <p className="sc-muted sc-small" style={{ padding: "var(--space-3) var(--space-4)" }}>Mindestbetrag für Auszahlung {formatEuro(vorschau.data!.mindestbetragCent)} (<Link to="/laden/einstellungen/abrechnung">Einstellungen › Abrechnung</Link>). Beträge darunter werden in die nächste Periode übertragen; negative Ergebnisse werden als Forderung geführt.</p>
              </Card>
            )}
          </>
        )}
      </Seitenzustand>
      {abrechnungen.data?.abrechnungen.length ? null : null}
    </>
  );
}
